import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';

interface Report {
    id: string;
    name: string;
    description: string;
    category: string;
    formats: string[];
}

interface GeneratedReport {
    reportId: string;
    reportName: string;
    format: string;
    status: string;
    jobId: string;
}

export default function ReportsPage() {
    const { t } = useTranslation();
    const [reports, setReports] = useState<Report[]>([]);

    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState<string | null>(null);
    const [selectedFormat, setSelectedFormat] = useState<Record<string, string>>({});
    const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
    const [showSuccess, setShowSuccess] = useState<string | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const reportsRes = await api.get('/api/v1/reports');
            setReports(reportsRes.data || []);

            const formats: Record<string, string> = {};
            (reportsRes.data || []).forEach((r: Report) => {
                // Filtramos CSV si viene del backend
                r.formats = r.formats.filter(f => f !== 'CSV');
                formats[r.id] = r.formats[0] || 'PDF';
            });
            setSelectedFormat(formats);
        } catch (error) {
            console.error('Error loading reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (report: Report) => {
        console.log('[Reports] handleGenerate called:', report.id);
        try {
            setGenerating(report.id);
            const format = selectedFormat[report.id] || 'PDF';
            const filename = `${report.id}.${format === 'EXCEL' ? 'xlsx' : 'pdf'}`;

            // 1. Pedir generación al backend Java
            const genRes = await api.post('/api/v1/reports/generate', {
                reportId: report.id,
                format,
            });
            console.log('[Reports] generate response:', genRes.status, genRes.data);

            const fileId = genRes.data?.fileId || genRes.data?.id || genRes.data?.jobId;
            if (!fileId) {
                throw new Error('Respuesta sin fileId/jobId');
            }

            // 2. Descargar el archivo
            const dl = await api.get(`/api/v1/reports/download/${fileId}`, {
                responseType: 'blob',
            });
            const blob = new Blob([dl.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            }, 100);

            // 3. UI feedback
            setGeneratedReports(prev => [{
                reportId: report.id,
                reportName: report.name,
                format,
                status: 'COMPLETED',
                jobId: String(fileId),
            }, ...prev]);
            setShowSuccess(report.id);
            setTimeout(() => setShowSuccess(null), 3000);
        } catch (error: any) {
            console.error('[Reports] generation error:', error?.response?.status, error?.response?.data, error);
            alert(t('reports.generateError', { status: error?.response?.status || t('reports.noResponse') }));
        } finally {
            setTimeout(() => setGenerating(null), 500);
        }
    };


    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'OPERATIONS': return 'from-blue-500 to-blue-600';
            case 'DECONFLICTION': return 'from-orange-500 to-orange-600';
            case 'EVALUATION': return 'from-purple-500 to-purple-600';
            case 'AGENCIES': return 'from-green-500 to-green-600';
            case 'GEOGRAPHICAL': return 'from-cyan-500 to-cyan-600';
            default: return 'from-gray-500 to-gray-600';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'OPERATIONS':
                return 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2';
            case 'DECONFLICTION':
                return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
            case 'EVALUATION':
                return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
            case 'GEOGRAPHICAL':
                return 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7';
            default:
                return 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('reports.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('reports.subtitle')}</p>
                </div>
                <button
                    onClick={loadData}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('reports.refresh')}
                </button>
            </div>

            {generatedReports.filter(r => r.status === 'COMPLETED').length > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <div className="text-green-400 font-medium">{t('reports.generatedTitle')}</div>
                            <div className="text-green-300/70 text-sm">
                                {generatedReports.filter(r => r.status === 'COMPLETED').map(r =>
                                    `${r.reportName} (${r.format})`
                                ).join(', ')}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div>
                <h2 className="text-lg font-semibold text-white mb-4">{t('reports.available')}</h2>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {reports.map((report) => (
                            <div
                                key={report.id}
                                className={`bg-gray-800/50 rounded-xl border overflow-hidden transition-all ${showSuccess === report.id
                                    ? 'border-green-500/50 ring-2 ring-green-500/20'
                                    : 'border-gray-700/50 hover:border-gray-600'
                                    }`}
                            >
                                <div className={`h-2 bg-gradient-to-r ${getCategoryColor(report.category)}`}></div>
                                <div className="p-5">
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${getCategoryColor(report.category)} bg-opacity-20`}>
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getCategoryIcon(report.category)} />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-white">{report.name}</h3>
                                            <p className="text-sm text-gray-400 mt-0.5">{report.description}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-xs text-gray-500">{t('reports.format')}</span>
                                        <div className="flex gap-1">
                                            {report.formats.map((format) => (
                                                <button
                                                    key={format}
                                                    onClick={() => setSelectedFormat(prev => ({ ...prev, [report.id]: format }))}
                                                    className={`px-2 py-1 text-xs rounded transition-colors ${selectedFormat[report.id] === format
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                                                        }`}
                                                >
                                                    {format}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <button
                                        data-testid={`generate-report-${report.id}`}
                                        aria-label={t('reports.generateReportAria', { name: report.name })}
                                        onClick={() => handleGenerate(report)}
                                        disabled={generating === report.id}
                                        className={`w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${generating === report.id
                                            ? 'bg-blue-600/50 text-blue-300 cursor-wait'
                                            : showSuccess === report.id
                                                ? 'bg-green-600 text-white'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                            }`}
                                    >
                                        {generating === report.id ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                {t('reports.generating')}
                                            </>
                                        ) : showSuccess === report.id ? (
                                            <>
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t('reports.generated')}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                {t('reports.generate', { format: selectedFormat[report.id] || 'PDF' })}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </div>
    );
}
